import React, {
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import ClaimForm from '../../components/claimForm/claimForm';
import { useHistory, useRouteMatch } from 'react-router-dom';
import * as action from '../../actions';
// @ts-ignore
import _ from 'lodash';
import questionFunctions from '../../utils/functions/question';
import questionGroupStepProjections from '../../projections/questionGroupStep';
import questionProjections from '../../projections/question';
import codes from '../../utils/constants/codes';
import messageProjections from '../../projections/message';
import usePrevious from '../../hooks/usePrevious';
import useDebouncedAutoSave from '../../hooks/useDebouncedAutoSave';
import {
    clearClaimCategoryState,
    clearGroupStep,
    clearPolicyTypeCombination,
    CurrencySchema,
} from '../../actions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { ObjectOfAny, QuestionSchema } from '../../reducers/types';
import currencyProjections from '../../projections/currency';
import { useTranslation } from 'react-i18next';

type Props = {
    setHasRouteChangedByModal: (b: boolean) => void;
    hasRouteChangedByModal: boolean;
    fetchingQuestionGroupSteps: boolean;
    questionGroupsStepsLoading: boolean;
    setQuestionGroupsStepsLoading: (b: boolean) => any;
    prevFetchingQuestionGroupSteps: boolean | undefined;
};

const ClaimFormContainer = (props: Props): React.ReactElement | null => {
    const {
        setHasRouteChangedByModal,
        hasRouteChangedByModal,
        fetchingQuestionGroupSteps,
        questionGroupsStepsLoading,
        setQuestionGroupsStepsLoading,
        prevFetchingQuestionGroupSteps,
    } = props;
    const match = useRouteMatch();
    const activeMatch: any = useRouteMatch(`${match.path}/:step`);
    const prevActiveMatch: any = usePrevious(activeMatch);
    let history = useHistory();
    const { i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const questionGroupSteps = useAppSelector(
        state => state.questionGroupStep.questionGroupStep.byId,
    ) as { id: string; code: string; name: string }[];
    const questionTypes = useAppSelector(
        state => state.questionType.questionType.byId,
    ) as { id: string; code: string; name: string }[];
    const answers = useAppSelector(state => state.answer.answer) as ObjectOfAny;
    const allAnswersFromGroupSteps = useAppSelector(
        state => state.answer.answer.answerFromGroupSteps,
    ) as any | string;
    const questions = useAppSelector(
        state => state.question.question.byId,
    ) as QuestionSchema[];
    const tabs: any = useAppSelector(questionGroupStepProjections.tabs);
    const submittingClaim = useAppSelector(
        state => state.claimApplication.submitting,
    ) as boolean;
    const isDraftApplicationRequestFinished = useAppSelector(
        state => state.claimApplication.isDraftApplicationRequestFinished,
    ) as boolean;
    const prevSubmittingClaim = usePrevious(submittingClaim) as
        boolean | undefined;
    const totalErrorMessages = useAppSelector(
        messageProjections.totalErrorMessages,
    ) as number;
    const enableAutoSave = useAppSelector(
        state =>
            state.applicationSettings.apiApplicationSettings.enableAutoSave,
    ) as boolean;
    const prevTotalErrorMessages = usePrevious(totalErrorMessages) as
        number | undefined;
    const [activeTab, setActiveTab] = useState<null | string>(null);
    const [captchaValue, setCaptchaValue] = useState<null | string>(null);
    const recaptchaRef: RefObject<any> = React.createRef();
    const languageDependency = i18n.language;
    const activeGroupStepCode = useAppSelector(
        state => state.answer.answer.activeGroupStepCode,
    ) as string | null;
    const loadingQuestionGroupStep = useAppSelector(
        state => state.questionGroupStep.loading,
    ) as boolean;
    const prevLoadingQuestionGroupStep = usePrevious(
        loadingQuestionGroupStep,
    ) as boolean | undefined;
    const questionsExists = useAppSelector(
        questionProjections.questionsExists,
    ) as boolean;
    const currencyOptions = useAppSelector(currencyProjections.currencies) as
        CurrencySchema[] | null;
    const questionTypesAttempted = useAppSelector(
        state => state.questionType.attempted,
    ) as boolean;
    const questionTypesLoading = useAppSelector(
        state => state.questionType.loading,
    ) as boolean;
    const validationRulesAttempted = useAppSelector(
        state => state.questionValidationRule.attempted,
    ) as boolean;
    const validationRulesLoading = useAppSelector(
        state => state.questionValidationRule.loading,
    ) as boolean;
    const [selectedCurrency, setSelectedCurrency] = useState({
        code: '',
        sign: '',
    } as any);

    const fetchQuestionTypeAction = useCallback(
        () => dispatch(action.fetchQuestionTypes()),
        [dispatch],
    );

    const fetchQuestionValidationRuleAction = useCallback(
        () => dispatch(action.fetchQuestionValidationRule()),
        [dispatch],
    );

    const fetchCurrenciesAction = useCallback(
        () => dispatch(action.fetchCurrencies()),
        [dispatch],
    );

    const sendClaimApplicationAction = useCallback(
        (payload: action.sendClaimApplicationAction) =>
            dispatch(action.sendClaimApplication(payload)),
        [dispatch],
    );

    const updateClaimApplicationAction = useCallback(
        (payload: action.updateClaimApplicationAction) =>
            dispatch(action.updateClaimApplication(payload)),
        [dispatch],
    );

    const sendDraftClaimApplicationByUserAction = useCallback(
        (payload: action.sendDraftClaimApplicationByUserAction) =>
            dispatch(action.sendDraftClaimApplicationByUser(payload)),
        [dispatch],
    );

    const fetchDocumentTypesAction = useCallback(
        (payload: action.fetchDocumentTypesAction) =>
            dispatch(action.fetchDocumentTypes(payload)),
        [dispatch],
    );

    const fetchQuestionAction = useCallback(
        (payload: action.fetchQuestionsAction) =>
            dispatch(action.fetchQuestions(payload)),
        [dispatch],
    );

    const clearQuestionStateAction = useCallback(
        () => dispatch(action.clearQuestionState()),
        [dispatch],
    );

    const clearClaimCategoryStateAction = useCallback(
        () => dispatch(clearClaimCategoryState()),
        [dispatch],
    );

    const clearFormTypesStateAction = useCallback(
        () => dispatch(action.clearFormTypes()),
        [dispatch],
    );
    const clearPolicyTypeCombinationAction = useCallback(
        () => dispatch(clearPolicyTypeCombination()),
        [dispatch],
    );

    const clearGroupStepAction = useCallback(
        () => dispatch(clearGroupStep()),
        [dispatch],
    );

    // clear captcha token after failed submission
    useEffect(() => {
        if (
            !submittingClaim &&
            prevSubmittingClaim &&
            totalErrorMessages !== prevTotalErrorMessages &&
            recaptchaRef &&
            recaptchaRef.current &&
            recaptchaRef.current.reset
        ) {
            setCaptchaValue(null);
            recaptchaRef.current.reset();
        }
    }, [
        submittingClaim,
        prevSubmittingClaim,
        totalErrorMessages,
        prevTotalErrorMessages,
        recaptchaRef,
    ]);

    // Reference data — fetch once per session. The reducer's `attempted` flag
    // is set on REQUEST and never reset, so any container fetching the same
    // data sees a single source of truth and we never retry-storm a failing
    // endpoint.
    useEffect(() => {
        if (validationRulesAttempted || validationRulesLoading) return;
        fetchQuestionValidationRuleAction();
    }, [
        validationRulesAttempted,
        validationRulesLoading,
        fetchQuestionValidationRuleAction,
    ]);

    useEffect(() => {
        if (questionTypesAttempted || questionTypesLoading) return;
        fetchQuestionTypeAction();
    }, [questionTypesAttempted, questionTypesLoading, fetchQuestionTypeAction]);

    useEffect(() => {
        if (
            activeMatch &&
            activeMatch.params &&
            activeMatch.params.step &&
            tabs &&
            tabs.length > 0 &&
            tabs.some((e: { key: string }) => e.key === activeMatch.params.step)
        ) {
            setActiveTab(activeMatch.params.step);
        }
    }, [activeMatch, activeTab, questionGroupSteps]);

    //if modal has open one time to not clear all and after set it to true
    useEffect(() => {
        if (!activeMatch && prevActiveMatch && !hasRouteChangedByModal) {
            clearQuestionStateAction();
            clearClaimCategoryStateAction();
            clearFormTypesStateAction();
            clearPolicyTypeCombinationAction();
            clearGroupStepAction();
        } else {
            setHasRouteChangedByModal(false);
        }
    }, [activeMatch, prevActiveMatch]);

    // fetch questions of group after changing tab
    useEffect(() => {
        const shouldFetchForStep =
            (activeMatch &&
                prevActiveMatch &&
                activeMatch.params.step !== prevActiveMatch.params.step &&
                questionGroupSteps[activeMatch.params.step]) ||
            (activeMatch &&
                !prevActiveMatch &&
                questionGroupSteps[activeMatch.params.step]) ||
            (activeMatch &&
                prevLoadingQuestionGroupStep &&
                !loadingQuestionGroupStep &&
                (activeGroupStepCode || languageDependency) &&
                questionGroupSteps[activeMatch.params.step]);
        if (answers.policyType && shouldFetchForStep) {
            fetchQuestionAction({
                policyTypeCode: answers.policyType,
                claimCategoryCode: answers.claimCategory,
                formTypeCode: answers.formType,
                questionGroupStepId:
                    questionGroupSteps[activeMatch.params.step].id,
            });

            fetchDocumentTypesAction({
                // @ts-ignore
                policyTypeCode: answers.policyType,
                claimCategoryCode: answers.claimCategory,
                formTypeCode: answers.formType,
            });
        }
    }, [
        activeMatch,
        answers,
        prevActiveMatch,
        questionGroupSteps,
        activeGroupStepCode,
        loadingQuestionGroupStep,
        prevLoadingQuestionGroupStep,
        fetchQuestionAction,
        languageDependency,
    ]);

    // everytime the questions have loaded and store to state,ensure that the loader is false anyway.
    useEffect(() => {
        setQuestionGroupsStepsLoading(false);
    }, [fetchQuestionAction]);

    useEffect(() => {
        if (currencyOptions === null) {
            fetchCurrenciesAction();
        }
    }, [currencyOptions, fetchCurrenciesAction]);

    useEffect(() => {
        if (currencyOptions !== null) {
            const defaultCurrency = currencyOptions.find(
                c => c.isDefault === true,
            );
            setSelectedCurrency({
                code: defaultCurrency?.name,
                sign: defaultCurrency?.symbol,
            });
        }
    }, [currencyOptions]);

    const onNextPage = (values: { [x: string]: any[] }): undefined => {
        if (
            activeTab === codes.attachments &&
            values &&
            values[codes.claimAttachments] &&
            values[codes.claimAttachments].some(attachment =>
                questionFunctions.validateAttachment(attachment),
            )
        ) {
            return;
        }

        const index = tabs.findIndex(
            (tab: { key: string | null }) => tab.key === activeTab,
        );
        const nextTabCode = tabs[index + 1].key;
        history.push(nextTabCode);
    };

    const onPreviousPage = (): void => {
        const index = tabs.findIndex(
            (tab: { key: string | null }) => tab.key === activeTab,
        );
        setActiveTab(tabs[index - 1].code);
        history.push(tabs[index - 1].key);
    };

    const lastSavedTabRef = useRef<string | null>(null);
    const savedAnsweredCountByTabRef = useRef<{ [tab: string]: number }>({});

    const buildDraftPayload = useCallback(
        (values: any, errors: any) => {
            if (!enableAutoSave) {
                return null;
            }
            if (!activeTab || activeTab === codes.declarationGroupStep) {
                return null;
            }
            if (!errors || errors.error) {
                return null;
            }

            const tabQuestions = Object.values(
                questions || {},
            ) as QuestionSchema[];
            const requiredQuestions = tabQuestions.filter((q: any) => {
                try {
                    return q.requiredByOptionId
                        ? questionFunctions.requiredQuestionByOptionId(
                              q,
                              values,
                              questions,
                          )
                        : q.isRequired;
                } catch {
                    return false;
                }
            });

            const answeredRequired = requiredQuestions.filter((q: any) => {
                const v = values[q.code];
                if (v === undefined || v === null || v === '') return false;
                if (Array.isArray(v) && v.length === 0) return false;
                return true;
            }).length;

            // Attachments tab bypasses progress-gating: every add/remove is meaningful
            // even though it doesn't tick up the answered-required count.
            const isAttachmentsTab = activeTab === codes.attachments;
            const lastSavedCount =
                savedAnsweredCountByTabRef.current[activeTab] ?? 0;
            const tabChanged =
                lastSavedTabRef.current !== null &&
                lastSavedTabRef.current !== activeTab;
            const progressIncreased = answeredRequired > lastSavedCount;
            if (!isAttachmentsTab && !tabChanged && !progressIncreased)
                return null;

            savedAnsweredCountByTabRef.current[activeTab] = Math.max(
                lastSavedCount,
                answeredRequired,
            );
            lastSavedTabRef.current = activeTab;

            const currency = currencyOptions?.find(
                c =>
                    c.code.toLowerCase() ===
                    selectedCurrency.code.toLowerCase(),
            );
            const answersArray = questionFunctions.constructAnswersDTO(
                questions,
                questionTypes,
                _.pickBy(values, (val: any, key: number) => !!questions[key]),
                currency,
                allAnswersFromGroupSteps,
            );
            return {
                data: {
                    PolicyTypeCode: answers.policyType,
                    ClaimCategoryCode:
                        answers.claimCategory &&
                        answers.claimCategory.length > 0
                            ? answers.claimCategory
                            : '___',
                    FormTypeCode:
                        answers.formType && answers.formType.length > 0
                            ? answers.formType
                            : '___',
                    ClaimApplicationId: answers.claimApplicationId,
                    Answers: answersArray,
                },
            };
        },
        [
            enableAutoSave,
            activeTab,
            currencyOptions,
            selectedCurrency,
            questions,
            questionTypes,
            allAnswersFromGroupSteps,
            answers,
        ],
    );

    const { onAutoSave: rawOnAutoSaveDraftClaim, flushAutoSave } =
        useDebouncedAutoSave<
            any,
            any,
            action.sendDraftClaimApplicationByUserAction
        >({
            send: sendDraftClaimApplicationByUserAction,
            buildPayload: buildDraftPayload,
            isInFlight: !isDraftApplicationRequestFinished,
        });

    // On the attachments tab, fire the save immediately (no debounce wait) since
    // attachment adds/removes are imperative actions, not blur-driven typing.
    const onAutoSaveDraftClaim = useCallback(
        (values: any, errors: any) => {
            rawOnAutoSaveDraftClaim(values, errors);
            if (activeTab === codes.attachments) {
                flushAutoSave();
            }
        },
        [rawOnAutoSaveDraftClaim, flushAutoSave, activeTab],
    );

    // On tab change: initialize lastSavedTab on first mount so tabChanged works,
    // then flush any pending save from the previous tab. The next interaction on
    // the new tab will trigger its own save naturally.
    useEffect(() => {
        if (!activeTab) {
            return;
        }
        if (lastSavedTabRef.current === null) {
            lastSavedTabRef.current = activeTab;
            return;
        }
        if (lastSavedTabRef.current !== activeTab) {
            flushAutoSave();
        }
    }, [activeTab, flushAutoSave]);

    const onSubmitForm = (values: any) => {
        const currency = currencyOptions?.find(
            c => c.code.toLowerCase() === selectedCurrency.code.toLowerCase(),
        );
        const answersArray = questionFunctions.constructAnswersDTO(
            questions,
            questionTypes,
            _.pickBy(values, (val: any, key: number) => !!questions[key]),
            currency,
            allAnswersFromGroupSteps,
        );

        if (answers.claimApplicationId) {
            updateClaimApplicationAction({
                data: {
                    ReCaptchaResponseToken: captchaValue,
                    PolicyTypeCode: answers.policyType,
                    ClaimCategoryCode:
                        answers.claimCategory &&
                        answers.claimCategory.length > 0
                            ? answers.claimCategory
                            : '___',
                    FormTypeCode:
                        answers.formType && answers.formType.length > 0
                            ? answers.formType
                            : '___',
                    Answers: answersArray,
                    ClaimApplicationId: answers.claimApplicationId,
                },
            });
        } else {
            sendClaimApplicationAction({
                data: {
                    ReCaptchaResponseToken: captchaValue,
                    PolicyTypeCode: answers.policyType,
                    ClaimCategoryCode:
                        answers.claimCategory &&
                        answers.claimCategory.length > 0
                            ? answers.claimCategory
                            : '___',
                    FormTypeCode:
                        answers.formType && answers.formType.length > 0
                            ? answers.formType
                            : '___',
                    Answers: answersArray,
                },
            });
        }
    };

    const handleCurrencySelectWrapper = (
        currency: null | string | any,
        values: any,
    ) => {
        setSelectedCurrency(currency);
        onAutoSaveDraftClaim(values, currency);
    };

    if (!questionsExists) {
        return null;
    }

    return (
        <ClaimForm
            questionGroupsStepsLoading={questionGroupsStepsLoading}
            setQuestionGroupsStepsLoading={setQuestionGroupsStepsLoading}
            fetchingQuestionGroupSteps={fetchingQuestionGroupSteps}
            activeTab={activeTab}
            tabs={tabs}
            questionTypes={questionTypes}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            onSubmitForm={onSubmitForm}
            handleCurrencySelectWrapper={handleCurrencySelectWrapper}
            selectedCurrency={selectedCurrency}
            currencyOptions={currencyOptions}
            captchaValue={captchaValue}
            setCaptchaValue={setCaptchaValue}
            recaptchaRef={recaptchaRef}
            onAutoSaveDraftClaim={onAutoSaveDraftClaim}
            isDraftApplicationRequestFinished={
                isDraftApplicationRequestFinished
            }
            loadingQuestionGroupStep={loadingQuestionGroupStep}
            prevFetchingQuestionGroupSteps={prevFetchingQuestionGroupSteps}
        />
    );
};

export default ClaimFormContainer;
